// bounded.onramp() - fund the signed-in user's wallet with Coinbase Onramp.
//
// Web-only. Opens Coinbase's hosted checkout (pay.coinbase.com) in a popup
// (new-tab fallback) using a single-use session token minted by the Bounded
// platform at POST <origin>/__bounded/onramp/session on the app's own origin.
// The platform holds the Coinbase credential; the app never sees it. The
// session is bound server-side to the caller's verified Bounded JWT, and the
// destination defaults to that user's embedded wallet.
//
// Amount semantics:
//   amountUsd    - exact fiat in; the fee/output breakdown comes back as `quote`.
//   minAmountOut - a FLOOR on crypto received. The platform quotes Coinbase's
//     worst-fee payment method (card) and pads by `slippagePad` (default 1%),
//     so cheaper methods overshoot the floor rather than undershooting it. The
//     floor holds unless the market moves more than the pad while the user sits
//     on the checkout screen - which is why completion is VERIFIED: after the
//     popup closes we poll the platform's status leg and resolve with the
//     actual filled amount when Coinbase reports it.
//
// Works on Bounded-served origins (<app>.bounded.page / custom domains). On a
// local dev origin the session endpoint does not exist; we fail with a clear
// error rather than guessing a host.
import { getFreshAuthToken, getSessionGeneration } from '@bounded-sh/core';
import { getCurrentUser } from './global';
function hasDOM() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
/**
 * DS3-0415: force one token refresh for a 401 recovery, bound to the session that
 * authenticated the original attempt. Returns the refreshed bearer only when the retry
 * may proceed under the SAME login; otherwise `null`, so the caller surfaces the original
 * 401 rather than replaying the request under a principal that replaced the original
 * mid-flight. Mirrors builds/client.ts:refreshDevApiTokenOnce.
 */
async function refreshOnrampTokenOnce(capturedGeneration) {
    try {
        // force: the server already rejected the stored token, so a locally-fresh-looking
        // one is not evidence of anything - require a genuinely new credential.
        const token = await getFreshAuthToken(false, { force: true });
        if (!token)
            return null;
        // A refresh preserves the generation; a re-login mints a new one. An unchanged
        // generation covers both "still the same token" and "refreshed descendant", while
        // rejecting a retry that a concurrent login would otherwise run as someone else.
        const currentGeneration = await getSessionGeneration(false);
        if (currentGeneration !== capturedGeneration)
            return null;
        return token;
    }
    catch (_a) {
        // auth_changed or a definitively rejected refresh: the retry must not proceed.
        return null;
    }
}
async function authedFetch(path, init) {
    var _a, _b;
    // DS3-0415: acquire the bearer through the same refresh-aware core path the HTTP
    // client uses (getFreshAuthToken), NOT the raw non-refreshing accessor. Bounded ID
    // tokens expire after ~1h while the refresh credential lives longer, so an onramp
    // surface left open past the expiry would otherwise send a stale token and take a 401
    // with no recovery. getFreshAuthToken refreshes when needed and binds to the session.
    const idToken = await getFreshAuthToken(false);
    if (!idToken)
        throw new Error('onramp: no active session - call login() first');
    // Bind any 401 recovery to the session that authenticated this first attempt.
    const capturedGeneration = await getSessionGeneration(false);
    const send = (bearer) => fetch(`${window.location.origin}${path}`, Object.assign(Object.assign({}, init), { headers: Object.assign(Object.assign({}, init === null || init === void 0 ? void 0 : init.headers), { Authorization: `Bearer ${bearer}` }) }));
    let response = await send(idToken);
    // A token that expires between the freshness read and the server reaching it is
    // rejected with 401. Force exactly one refresh bound to the captured generation and
    // retry once.
    if (response.status === 401) {
        const refreshed = await refreshOnrampTokenOnce(capturedGeneration);
        if (refreshed) {
            // Release the discarded 401 body before replaying to avoid an unconsumed-stream
            // warning / pooled-connection retention under undici / React Native.
            try {
                await ((_b = (_a = response.body) === null || _a === void 0 ? void 0 : _a.cancel) === null || _b === void 0 ? void 0 : _b.call(_a));
            }
            catch ( /* ignore */_c) { /* ignore */ }
            response = await send(refreshed);
        }
    }
    return response;
}
async function readError(res) {
    var _a;
    try {
        return (_a = (await res.json()).error) !== null && _a !== void 0 ? _a : '';
    }
    catch (_b) {
        return '';
    }
}
function sessionBody(options, extra) {
    return JSON.stringify(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (options.asset ? { asset: options.asset } : {})), (options.amountUsd !== undefined ? { amountUsd: options.amountUsd } : {})), (options.minAmountOut !== undefined ? { minAmountOut: options.minAmountOut } : {})), (options.slippagePad !== undefined ? { slippagePad: options.slippagePad } : {})), extra));
}
function resolveAddress() {
    var _a;
    const user = getCurrentUser();
    const address = (_a = user === null || user === void 0 ? void 0 : user.address) !== null && _a !== void 0 ? _a : undefined;
    if (!address) {
        throw new Error(user
            ? 'onramp: the signed-in account has no wallet address (create one via the embedded wallet flow)'
            : 'onramp: no signed-in user - call login() first');
    }
    return address;
}
/**
 * Preview a quote (fees + exact/floor output) WITHOUT opening any window or
 * consuming a session. Show this to the user, then call onramp() to check out.
 */
export async function onrampQuote(options = {}) {
    if (!hasDOM())
        throw new Error('onrampQuote is web-only');
    resolveAddress();
    const res = await authedFetch('/__bounded/onramp/session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: sessionBody(options, { quoteOnly: true }),
    });
    if (!res.ok)
        throw new Error(`onrampQuote: failed (${res.status}${await readError(res).then((e) => (e ? `: ${e}` : ''))})`);
    return (await res.json());
}
/** The signed-in user's recent onramp transactions (newest first). */
export async function onrampStatus(sinceMs) {
    if (!hasDOM())
        throw new Error('onrampStatus is web-only');
    const res = await authedFetch(`/__bounded/onramp/status${sinceMs ? `?since=${Math.floor(sinceMs)}` : ''}`);
    if (!res.ok)
        throw new Error(`onrampStatus: failed (${res.status})`);
    return (await res.json()).transactions;
}
function isSuccess(t) {
    return /SUCCESS|COMPLETED/i.test(t.status);
}
function isPending(t) {
    return /PROGRESS|PENDING|CREATED/i.test(t.status);
}
async function verifyCompletion(openedAt, asset, timeoutMs) {
    const deadline = Date.now() + timeoutMs;
    // 60s of clock-skew slack on `since`: Coinbase stamps server-side.
    const since = openedAt - 60000;
    for (;;) {
        let sawPending = false;
        try {
            const txs = await onrampStatus(since);
            const done = txs.find((t) => isSuccess(t) && (t.asset === null || t.asset === asset));
            if (done)
                return done;
            sawPending = txs.some(isPending);
        }
        catch ( /* transient - keep polling until the deadline */_a) { /* transient - keep polling until the deadline */ }
        // No completed fill and nothing in flight: one grace poll cycle still
        // runs (Coinbase's record can lag the redirect), but don't burn the
        // full window when the user plainly abandoned checkout.
        if (Date.now() >= deadline)
            return null;
        if (!sawPending && Date.now() >= openedAt + 12000 && Date.now() - openedAt > timeoutMs / 2)
            return null;
        await new Promise((r) => setTimeout(r, 3000));
    }
}
/**
 * Open Coinbase Onramp for the signed-in user. Resolves after the window
 * closes, with completion VERIFIED against Coinbase when possible.
 * Call from a click handler - popup blockers require user activation.
 */
export async function onramp(options = {}) {
    var _a;
    if (!hasDOM())
        throw new Error('onramp() is web-only');
    resolveAddress();
    // Open the window synchronously - BEFORE any await - so the click's transient
    // user-activation is not consumed by the session fetch below. A window.open
    // issued after the fetch is rejected by strict popup blockers even from a
    // click handler, because activation can expire across the awaited round trip.
    // This holds for BOTH the popup and the documented new-tab mode (same
    // hard-won pattern as loginWithPopup). We navigate this placeholder to the
    // checkout URL once the session is minted; if no handle can be opened
    // synchronously we require a fresh user action rather than a delayed open.
    let handle;
    if (options.newTab) {
        handle = window.open('', '_blank');
    }
    else {
        const w = options.width || 460;
        const h = options.height || 720;
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;
        handle = window.open('', 'bounded-onramp', `width=${w},height=${h},left=${left},top=${top}`);
    }
    if (!handle)
        throw new Error('onramp: window.open was blocked - call onramp() from a click handler');
    let session;
    try {
        const res = await authedFetch('/__bounded/onramp/session', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: sessionBody(options),
        });
        if (!res.ok) {
            const detail = await readError(res);
            if (res.status === 404) {
                throw new Error('onramp: session endpoint not found - onramp works on Bounded-served origins (your deployed app URL), not local dev servers');
            }
            throw new Error(`onramp: session mint failed (${res.status}${detail ? `: ${detail}` : ''})`);
        }
        session = (await res.json());
    }
    catch (error) {
        try {
            handle.close();
        }
        catch ( /* ignore */_b) { /* ignore */ }
        throw error;
    }
    const openedAt = Date.now();
    const base = Object.assign({ address: session.address, asset: session.asset }, (session.quote ? { quote: session.quote } : {}));
    // Navigate the pre-opened placeholder to the checkout URL.
    try {
        handle.location.replace(session.url);
    }
    catch (_c) {
        try {
            handle.location.href = session.url;
        }
        catch (_d) {
            try {
                handle.close();
            }
            catch ( /* ignore */_e) { /* ignore */ }
            throw new Error('onramp: window failed to navigate - retry from a fresh user action (click)');
        }
    }
    if (options.newTab) {
        // Cross-origin tab close is not observable, so resolve immediately;
        // verify completion later via onrampStatus().
        return Object.assign(Object.assign({}, base), { status: 'opened' });
    }
    // Popup mode: wait for the window to close, then VERIFY completion.
    await new Promise((resolve) => {
        const timer = setInterval(() => {
            if (handle.closed) {
                clearInterval(timer);
                resolve();
            }
        }, 500);
    });
    const fill = await verifyCompletion(openedAt, session.asset, (_a = options.completionTimeoutMs) !== null && _a !== void 0 ? _a : 24000);
    if (fill) {
        return Object.assign(Object.assign(Object.assign(Object.assign({}, base), { status: 'completed' }), (fill.amountOut !== null ? { actualOut: fill.amountOut } : {})), (fill.txHash ? { txHash: fill.txHash } : {}));
    }
    return Object.assign(Object.assign({}, base), { status: 'closed' });
}
